import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { cleanAIContent } from '../../lib/content-utils'

/** Mdast-like heading node shape (children may have value or nested children). */
interface HeadingLike {
  children?: Array<{ value?: string; children?: Array<{ value?: string }> }>
}

export interface MarkdownSource {
  id: number
  url: string
  title?: string
}

interface MarkdownRendererProps {
  content: string
  className?: string
  cleanPreface?: boolean
  sources?: MarkdownSource[]
}

/**
 * Slugify heading text so TOC anchors match: lowercase, no accents, hyphens.
 * Must match the logic used by extractHeadings (content-utils) for TOC links.
 */
function slugifyHeading(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim()
    .replace(/[^\p{L}\p{N}\s-]/gu, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '') || 'heading'
}

/**
 * Extract plain text from an mdast heading node (handles text, inlineCode, emphasis, strong).
 */
function getHeadingText(node: HeadingLike | undefined): string {
  if (!node?.children?.length) return ''
  return node.children
    .map((child) => {
      if (typeof child.value === 'string') return child.value
      if (Array.isArray(child.children)) {
        return child.children.map((c) => c.value ?? '').join('')
      }
      return ''
    })
    .join('')
}

/**
 * Renders markdown content with proper formatting using Tailwind Typography.
 * Optionally cleans AI conversational prefaces before rendering.
 * Headings h1–h3 get id attributes (slugs) for TOC anchor links.
 * Citation references [1], [2], ... are turned into links to #fuente-1, #fuente-2 for clickable refs.
 */
export function MarkdownRenderer({
  content,
  className = '',
  cleanPreface = true,
  sources,
}: MarkdownRendererProps) {
  let processedContent = cleanPreface ? cleanAIContent(content) : content
  // Post-process [N] citation refs: link to sources[n-1].url if available, else #fuente-N
  processedContent = processedContent.replace(
    /\[(\d+)\](?!\()/g,
    (_, n) => {
      const num = parseInt(n, 10)
      const src = sources?.[num - 1]
      if (src?.url) {
        const titleAttr = src.title != null && src.title !== '' && !src.title.includes('"')
          ? ` "${src.title}"`
          : ''
        return `[\\[${n}\\]](${src.url}${titleAttr})`
      }
      return `[\\[${n}\\]](#fuente-${n})`
    }
  )

  return (
    <div className={`prose prose-slate max-w-none ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Custom heading styles with id (slug) for TOC anchors
          h1: ({ node, children }) => {
            const id = slugifyHeading(getHeadingText(node as HeadingLike | undefined))
            return (
              <h1 id={id} className="text-2xl font-bold text-slate-900 mt-6 mb-4 first:mt-0">
                {children}
              </h1>
            )
          },
          h2: ({ node, children }) => {
            const id = slugifyHeading(getHeadingText(node as HeadingLike | undefined))
            return (
              <h2 id={id} className="text-xl font-bold text-slate-800 mt-5 mb-3 first:mt-0">
                {children}
              </h2>
            )
          },
          h3: ({ node, children }) => {
            const id = slugifyHeading(getHeadingText(node as HeadingLike | undefined))
            return (
              <h3 id={id} className="text-lg font-semibold text-slate-800 mt-4 mb-2 first:mt-0">
                {children}
              </h3>
            )
          },
          // Links open in new tab
          a: ({ href, children, title }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              title={title ?? undefined}
              className="text-emerald-600 hover:text-emerald-700 underline decoration-emerald-300 hover:decoration-emerald-500 transition-colors"
            >
              {children}
            </a>
          ),
          // Blockquotes styled to match brand
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-emerald-400 pl-4 my-4 text-slate-600 italic bg-slate-50 py-2 pr-4 rounded-r-lg">
              {children}
            </blockquote>
          ),
          // Code blocks
          code: ({ className, children, ...props }) => {
            const isInline = !className
            if (isInline) {
              return (
                <code className="bg-slate-100 text-emerald-700 px-1.5 py-0.5 rounded text-sm font-mono" {...props}>
                  {children}
                </code>
              )
            }
            return (
              <code className={`${className} block bg-slate-900 text-slate-100 p-4 rounded-lg text-sm font-mono overflow-x-auto`} {...props}>
                {children}
              </code>
            )
          },
          // Lists
          ul: ({ children }) => (
            <ul className="list-disc pl-6 my-3 space-y-1 text-slate-700">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal pl-6 my-3 space-y-1 text-slate-700">
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li className="text-slate-700 leading-relaxed">{children}</li>
          ),
          // Paragraphs
          p: ({ children }) => (
            <p className="text-slate-700 leading-relaxed my-3 first:mt-0 last:mb-0">
              {children}
            </p>
          ),
          // Horizontal rules
          hr: () => <hr className="my-6 border-slate-200" />,
          // Strong/emphasis
          strong: ({ children }) => (
            <strong className="font-semibold text-slate-900">{children}</strong>
          ),
          em: ({ children }) => (
            <em className="italic text-slate-600">{children}</em>
          ),
          // Tables (GFM)
          table: ({ children }) => (
            <div className="overflow-x-auto my-4">
              <table className="min-w-full border border-slate-200 rounded-lg">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-slate-50">{children}</thead>
          ),
          th: ({ children }) => (
            <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase border-b border-slate-200">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="px-4 py-2 text-sm text-slate-700 border-b border-slate-100">
              {children}
            </td>
          ),
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  )
}
